/** @jsx jsx */

/** @jsxFrag React.Fragment */

/**
 * List of programs that the learner has joined or been invited to
 */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';
import { SHORT_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Dialog, Grid, PageGridContainer, Tag, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import { useProgramJoinMutation } from 'bundles/enterprise-legacy-learner-home/queryHooks/useProgramJoinMutation';
import OrgLandingContext from 'bundles/org-landing/components/OrgLandingContext';
import type {
  EnterpriseProgram,
  ProgramInvitation,
  ProgramMembership,
  SoftDeletedProgramNode as SoftDeletedProgram,
} from 'bundles/org-landing/components/utils/useOrgLandingQuery';

import _t from 'i18n!nls/org-landing';

const styles = {
  root: css`
    margin-top: var(--cds-spacing-400);
  `,

  header: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: var(--cds-spacing-400) var(--cds-spacing-600);
  `,

  programContainer: css`
    overflow-x: hidden;

    & > div:nth-child(odd) {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }

    & > div:nth-child(even) {
      background-color: var(--cds-color-neutral-background-primary);
    }
  `,

  program: css`
    margin-left: calc(var(--cds-spacing-600) * -1);
    margin-right: calc(var(--cds-spacing-600) * -1);
    padding: var(--cds-spacing-400) var(--cds-spacing-600);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,

  programAction: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      margin-top: var(--cds-spacing-300);
      margin-bottom: var(--cds-spacing-300);

      a {
        width: 100%;
        max-width: inherit;
      }
    }
  `,

  title: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  `,

  logo: css`
    width: 72px;
    min-width: 72px;
    height: 72px;
    min-height: 72px;
    margin-right: var(--cds-spacing-200);
    background-size: contain;
    background-repeat: no-repeat;
  `,

  tagline: css`
    margin-top: var(--cds-spacing-200);
  `,

  confirmDialogActions: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    & > button {
      margin-right: var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;

      & > button {
        width: 100%;
        margin-right: 0;
        margin-bottom: var(--cds-spacing-300);
        max-width: inherit;
      }
    }
  `,
};

type Props = {
  header?: string | null;
  enterprisePrograms: Array<EnterpriseProgram>;
  programInvitations: Array<ProgramInvitation>;
  softDeletedPrograms: Array<SoftDeletedProgram>;
  membershipsMap: Record<string, ProgramMembership>;
};

type ProgramItemProps = {
  // we only need `name` and `description` from the metadata
  program: {
    id: EnterpriseProgram['id'];
    metadata: Pick<EnterpriseProgram['metadata'], 'name' | 'slug' | 'tagline' | 'logo' | 'squareLogo'>;
  };

  date?: number | string | null;

  // flag to indicate if this "MyProgram" is an invitation
  isInvitation?: boolean;

  isJoinLoading?: boolean;

  isSoftDeleted?: boolean;

  onContinueOrJoinClick?: (program: ProgramItemProps['program']) => void;
};

function ProgramItem({
  program,
  date,
  isInvitation,
  isJoinLoading,
  isSoftDeleted,
  onContinueOrJoinClick,
}: ProgramItemProps) {
  const track = useRetracked();
  const { metadata } = program;
  const logo = metadata.squareLogo || metadata.logo;

  const { enableOrgHomepageAutoJoinProgram } = React.useContext(OrgLandingContext);

  // format to yyyy/mm/dd
  const formattedDate = date ? formatDateTimeDisplay(date, SHORT_DATE_ONLY_DISPLAY) : null;

  const programPath = `/programs/${metadata.slug}`;

  // the handler only track events, and delegate the actual actions to the caller (MyProgram)
  const handleContinueOrJoinClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    track({
      trackingName: isInvitation ? 'org_landing_join_program' : 'org_landing_continue_program',
      action: 'click',
      trackingData: {},
    });

    onContinueOrJoinClick?.(program);
  };

  const getButtonText = () => {
    if (enableOrgHomepageAutoJoinProgram) {
      return _t('Continue');
    }

    if (isInvitation) {
      return _t('Join');
    }

    if (isSoftDeleted) {
      return _t('Rejoin');
    }

    return _t('Continue');
  };

  return (
    <div key={program.id} css={styles.program}>
      <div>
        <div css={styles.title}>
          <div
            css={[
              styles.logo,
              logo ? { backgroundImage: `url(${logo})` } : { backgroundColor: 'var(--cds-color-neutral-primary)' },
            ]}
          />
          <div>
            {formattedDate && (
              <>
                {enableOrgHomepageAutoJoinProgram || !isInvitation ? (
                  <Tag variant="status" priority="primary" color="highlight1">
                    {_t('Joined #{date}', { date: formattedDate })}
                  </Tag>
                ) : (
                  <Tag variant="status" priority="primary">
                    {_t('Invited #{date}', { date: formattedDate })}
                  </Tag>
                )}
              </>
            )}

            <Typography variant="h2semibold">{program.metadata.name}</Typography>
          </div>
        </div>
        <div css={styles.tagline}>
          {program.metadata.tagline && (
            <Typography2 component="p" variant="bodyPrimary" color="supportText">
              {program.metadata.tagline}
            </Typography2>
          )}
        </div>
      </div>
      <div css={styles.programAction}>
        <Button
          variant="secondary"
          component="a"
          href={programPath}
          onClick={handleContinueOrJoinClick}
          loading={isJoinLoading}
        >
          {getButtonText()}
        </Button>
      </div>
    </div>
  );
}

function MyPrograms({ header, enterprisePrograms, programInvitations, membershipsMap, softDeletedPrograms }: Props) {
  const [isJoinAllLoading, setIsJoinAllLoading] = React.useState(false);
  const [isProgramJoinLoading, setIsProgramJoinLoading] = React.useState<Record<string, boolean>>({});
  const [isJoinAllFailed, setIsJoinAllFailed] = React.useState(false);
  const [isJoinAllConfirmOpen, setIsJoinAllConfirmOpen] = React.useState(false);

  const { enableOrgHomepageAutoJoinProgram } = React.useContext(OrgLandingContext);
  const { programJoinMutation } = useProgramJoinMutation();

  const joinProgram = React.useCallback(
    async (programId: string) => {
      const response = await programJoinMutation({
        programId,
      });
      return response;
    },
    [programJoinMutation]
  );

  const joinMultiPrograms = React.useCallback(
    async (programIds: string[]) => {
      const promises = programIds.map((programId) => joinProgram(programId));
      return Promise.all(promises);
    },
    [joinProgram]
  );

  const handleJoinAll = async () => {
    const programIds = programInvitations
      .map((invitation) => invitation.userRoster?.program?.id)
      .filter(Boolean) as string[];

    try {
      setIsJoinAllConfirmOpen(false);
      setIsJoinAllLoading(true);
      // set all invitations to loading
      setIsProgramJoinLoading(programIds.reduce((acc, programId) => ({ ...acc, [programId]: true }), {}));
      await joinMultiPrograms(programIds);
      window.location.reload();
    } catch {
      setIsJoinAllFailed(true);

      // note: we don't want to hide the loading state if join success since the page will reload
      // so don't do `setIsJoinAllLoading(false);` in `finally` block
      setIsJoinAllLoading(false);

      // clear program loading
      setIsProgramJoinLoading({});
    }
  };

  const handleJoinClick = (program: ProgramItemProps['program']) => {
    // if enableOrgHomepageAutoJoinProgram is true, we join the program, otherwise redirect directly to the program
    if (enableOrgHomepageAutoJoinProgram) {
      setIsProgramJoinLoading((prevState) => ({ ...prevState, [program.id]: true }));

      // if the user clicked the "Join/Continue" and joined the program, then returned to this page by using the browser back button,
      // the invitation status will not be refreshed, and it is possible to join an already joined program.
      // in this case `joinProgram` will throw an 400 exception.
      // We don't really care about the error, since eventually the user will be redirected to the program page.
      // so we ignore the error here.
      joinProgram(program.id).finally(() => {
        setIsProgramJoinLoading((prevState) => ({ ...prevState, [program.id]: false }));
        window.location.assign(`/programs/${program.metadata.slug}`);
      });
    } else {
      window.location.assign(`/programs/${program.metadata.slug}`);
    }
  };

  const handleContinueClick = (program: ProgramItemProps['program']) => {
    window.location.assign(`/programs/${program.metadata.slug}`);
  };

  return (
    <div css={styles.root}>
      <PageGridContainer maxWidth="lg">
        <Grid item xs={12} css={styles.header}>
          <Typography variant="h1semibold">{header || _t('My Programs')}</Typography>
          {programInvitations.length > 0 && !enableOrgHomepageAutoJoinProgram && (
            <Button variant="primary" onClick={() => setIsJoinAllConfirmOpen(true)} loading={isJoinAllLoading}>
              {_t('Join all')}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} css={styles.programContainer}>
          {softDeletedPrograms.map((program) => {
            return (
              <ProgramItem
                program={{
                  id: program?.program?.id ?? '',
                  metadata: {
                    name: program?.program?.name ?? '',
                    slug: program?.program?.slug ?? '',
                    tagline: program?.program?.tagline ?? '',
                    logo: program?.program?.squareLogo ?? '',
                    squareLogo: program?.program?.squareLogo ?? '',
                  },
                }}
                isSoftDeleted
                key={program?.program?.id ?? ''}
                onContinueOrJoinClick={handleJoinClick}
              />
            );
          })}
          {programInvitations.map(
            (invitation) =>
              invitation.userRoster?.program && (
                <ProgramItem
                  program={invitation.userRoster.program}
                  date={invitation.createdAt}
                  isInvitation
                  isJoinLoading={isProgramJoinLoading[invitation.userRoster.program.id]}
                  key={invitation.userRoster.program.id}
                  onContinueOrJoinClick={handleJoinClick}
                />
              )
          )}
          {enterprisePrograms.map((program) => {
            return (
              <ProgramItem
                program={program}
                key={program.id}
                date={membershipsMap[program.id]?.createdAt}
                onContinueOrJoinClick={handleContinueClick}
              />
            );
          })}
        </Grid>
      </PageGridContainer>
      <Dialog open={isJoinAllFailed} onClose={() => setIsJoinAllFailed(false)}>
        <Dialog.HeadingGroup>{_t('Failed to join some programs')}</Dialog.HeadingGroup>
        <Dialog.Content>
          <Typography2 component="p" variant="bodyPrimary">
            {_t(
              'We were not able to join all programs at the moment. Please try to join individual programs manually.'
            )}
          </Typography2>
        </Dialog.Content>
        <Dialog.Actions>
          <Button variant="primary" onClick={() => setIsJoinAllFailed(false)}>
            {_t('OK')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      <Dialog open={isJoinAllConfirmOpen} onClose={() => setIsJoinAllConfirmOpen(false)}>
        <Dialog.HeadingGroup>{_t('Confirm action')}</Dialog.HeadingGroup>
        <Dialog.Content>
          <Typography2 component="p" variant="bodyPrimary">
            {_t('You are about to join all programs you have access to.')}
          </Typography2>
        </Dialog.Content>
        <Dialog.Actions css={styles.confirmDialogActions}>
          <Button variant="secondary" onClick={() => setIsJoinAllConfirmOpen(false)}>
            {_t('Cancel')}
          </Button>
          <Button variant="primary" onClick={handleJoinAll}>
            {_t('Ok')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
}

export default MyPrograms;
